import {
  PRESENCE_MSG,
  PRESENCE_ROOM,
  type DataChangedMessage,
  type FriendEventPush,
  type GameCounts,
  type GameCountsMessage,
  type PresenceSnapshot,
  type MaintenanceWarning,
  type PresenceEntry,
  type PresenceGameId,
  type PresenceUpdate,
  type SetActivityMessage,
} from "@bing/shared";
import { Client } from "colyseus.js";
import { manageRoom, type ConnectionRegistry } from "./lifecycle";
import { RECONNECT_RESYNC_SPREAD_MS } from "../store/badge-poll";
import { watchJoin } from "./join-watch";

export type {
  FriendEventPush,
  GameCounts,
  PresenceEntry,
  PresenceStatus,
  PresenceUpdate,
} from "@bing/shared";

/**
 * The presence socket, abstracted (like the DM/Hearts connectors) so the session
 * store's presence slice is testable with a fake connector — no WebSocket.
 *
 * `onSnapshot` seeds the caller's online friends on join; `onPresence` streams a
 * single friend's online/in-game/offline change; `onFriendEvent` delivers a live
 * friend request/accept aimed at the caller (→ refresh lists + toast);
 * `onGameCounts` streams the live per-game in-game player counts (a zero-filled
 * snapshot on join, then throttled broadcasts) that feed the lobby tiles.
 */
export interface PresenceHandlers {
  onSnapshot: (entries: PresenceEntry[]) => void;
  onPresence: (update: PresenceUpdate) => void;
  onFriendEvent: (push: FriendEventPush) => void;
  /** `online` = TOTAL users on presence (per-node) — the lobby's live player
   * counter; 0 when an older server omits the field (deploy window). */
  onGameCounts: (counts: GameCounts, online: number) => void;
  /** A pre-deploy maintenance warning fanned out to every in-app user (deploy-safety):
   * the app pops an apology modal. `etaMinutes` is an optional hint. */
  onMaintenance: (warning: MaintenanceWarning) => void;
  /**
   * «One of your slices moved — re-read it.» Carries the scope and nothing else; the client
   * re-reads the route it already had, conditionally, so an unchanged slice costs a 304.
   * This is the live path for everything the 20 s badge poll used to carry silently.
   */
  onDataChanged: (message: DataChangedMessage) => void;
  /**
   * Whether this socket is CONNECTED right now.
   *
   * The poll's cadence hangs off it: while presence is live the client keeps only a slow safety
   * net, and the moment the socket drops it falls back to the old fast interval, because with no
   * socket there are no pushes and the poll is the only thing keeping the badges alive.
   */
  onLive: (live: boolean) => void;
  onError: (message: string) => void;
}

export interface PresenceHandle {
  /** Tell the server this user entered (true) or left (false) a game room.
   * `game` names WHICH game while in-game (feeds the per-game lobby counters; «مار» too);
   * pass null when leaving (it is ignored server-side when `inGame` is false). */
  setInGame: (inGame: boolean, game?: PresenceGameId | null) => void;
  /**
   * The app moved to / from the FOREGROUND. Drives the server-side «in the app» marker the
   * API reads before it sends a Telegram PV — a backgrounded WebView keeps its socket alive,
   * so without this an invite that arrived while the phone was locked was pushed to a screen
   * nobody was watching and had its PV suppressed at the same time.
   */
  setForeground: (active: boolean) => void;
  leave: () => void;
}

export type PresenceConnector = (handlers: PresenceHandlers) => Promise<PresenceHandle>;

/** The real connector: joins the always-on Colyseus `presence` room over the WS. */
export function createColyseusPresenceConnector(opts: {
  wsUrl: string;
  getInitData: () => string | null;
  /** Lifecycle registry (injected in tests; defaults to the app singleton). */
  registry?: ConnectionRegistry;
  /** Injected in tests so the reconnect resync's spread is deterministic. */
  random?: () => number;
}): PresenceConnector {
  return async (handlers) => {
    const client = new Client(opts.wsUrl);
    const joinOptions = (): Record<string, unknown> => ({ initData: opts.getInitData() ?? "" });
    // The last activity this session declared — replayed onto every fresh
    // socket so a reconnect keeps the "in game" dot (and the per-game lobby
    // counter) truthful without asking the store anything.
    let lastActivity: SetActivityMessage | null = null;
    /**
     * HAS THIS SERVER TOLD US IT CAN PUSH? — the one thing `onLive(true)` may be said on.
     *
     * ⚠️ IT IS NOT «AM I CONNECTED». A socket connects perfectly well to a game-server that has
     * no `data-events` subscriber — one from before the batch, or one with no Redis — and the
     * client would then drop to the 120 s safety net while NOTHING is being pushed to it. That
     * is the deploy window the order `game-server → api → bot → web` exists to avoid, and it
     * reopens the moment anything is rolled back or shipped out of order. So the capability is
     * read off the join snapshot (`PresenceSnapshot.dataEvents`), and it is reset on every
     * reconnect cycle because the server on the other end may not be the one we left.
     */
    let canPush = false;
    /** Injectable only so `reconnect-herd`-style tests can pin the spread; the app uses Math. */
    const random = opts.random ?? Math.random;
    /** The pending, spread-out post-reconnect resync — cancelled if this connection is torn down. */
    let resync: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    /** The ONE door: `presenceLive` means «connected AND pushes arrive», never just connected. */
    const live = (connected: boolean): void => handlers.onLive(connected && canPush);

    const managed = manageRoom({
      name: "presence",
      registry: opts.registry,
      // Presence is best-effort background state: reconnects are SILENT (the
      // overlay is for game seats), and a plain rejoin fully re-seeds it (the
      // room re-sends the snapshot + counts on join).
      silent: true,
      initial: await watchJoin(
        { conn: "presence", kind: "presence" },
        client.joinOrCreate(PRESENCE_ROOM, joinOptions()),
      ),
      reacquire: () => client.joinOrCreate(PRESENCE_ROOM, joinOptions()),
      // The lifecycle reports every transition of THIS connection: "reconnecting" the moment a
      // cycle starts, "connected" when it lands, "failed" when it gives up. That is what the
      // client's poll cadence is keyed on — see `PresenceHandlers.onLive`.
      onStatus: (status) => {
        // A cycle that is not CONNECTED has no capability to speak of, and the socket we come
        // back on may be a different (older) server — so the claim is dropped here and only the
        // next snapshot can re-establish it.
        if (status !== "connected") canPush = false;
        live(status === "connected");
      },
      onReconnected: () => {
        if (lastActivity) managed.send(PRESENCE_MSG.setActivity, lastActivity);
        // A fresh socket has missed everything that happened while it was down, and the room
        // re-sends only the presence SNAPSHOT (the dots) — never the friend lists, the unread
        // counts or the inbox. Nothing resynced those before; the 20 s poll did, within 20 s.
        //
        // ⚠️ SPREAD, BECAUSE THIS IS THE ONE REFRESH EVERY CLIENT DOES AT THE SAME MOMENT. A
        // game-server recreate drops every presence socket at once, so every client runs this
        // three-route resync within a few seconds of every other — and a FAST recreate is the
        // WORST case, because most clients are still on their 500 ms / 1 s retry. Measured with
        // the shipped backoff (`reconnect-herd.test.ts`): at a 1.5 s outage **180 of 300 clients
        // land inside one second**, asking a single-threaded api for 5.31 s of work. The delay
        // is bounded well inside the 20 s tick that was the only mechanism here before, and any
        // push that arrives meanwhile is still applied instantly and coalesces with this.
        resync = setTimeout(() => {
          resync = null;
          if (disposed) return;
          handlers.onDataChanged({ scope: "friends" });
          handlers.onDataChanged({ scope: "dm" });
          handlers.onDataChanged({ scope: "inbox" });
        }, random() * RECONNECT_RESYNC_SPREAD_MS);
        // ⚠️ NOT `onLive(true)`. The rejoin re-sends the snapshot, and THAT is what decides
        // whether this server can push — a reconnect onto a rolled-back game-server must leave
        // the poll fast, not assert liveness because a socket opened.
        live(true);
      },
      wire: (room) => {
        room.onMessage(PRESENCE_MSG.snapshot, (msg: PresenceSnapshot) => {
          // The capability rides the snapshot, which the room sends on EVERY join (a friendless
          // user included), so this is the first message of every connection and of every
          // rejoin. An older server omits the field ⇒ `undefined` ⇒ the poll stays at 20 s.
          canPush = msg.dataEvents === true;
          live(true);
          handlers.onSnapshot(msg.friends);
        });
        room.onMessage(PRESENCE_MSG.presence, (update: PresenceUpdate) =>
          handlers.onPresence(update),
        );
        room.onMessage(PRESENCE_MSG.friendEvent, (push: FriendEventPush) =>
          handlers.onFriendEvent(push),
        );
        room.onMessage(PRESENCE_MSG.gameCounts, (msg: GameCountsMessage) =>
          handlers.onGameCounts(msg.counts, msg.online ?? 0),
        );
        room.onMessage(PRESENCE_MSG.maintenance, (msg: MaintenanceWarning) =>
          handlers.onMaintenance(msg ?? {}),
        );
        room.onMessage(PRESENCE_MSG.dataChanged, (msg: DataChangedMessage) =>
          handlers.onDataChanged(msg),
        );
        room.onError((code: number, message?: string) =>
          handlers.onError(message ?? `Error ${code}`),
        );
      },
    });

    return {
      setInGame: (inGame, game) => {
        // The game tag only means something while in-game; normalise to null otherwise.
        const message: SetActivityMessage = { inGame, game: inGame ? (game ?? null) : null };
        lastActivity = message;
        managed.send(PRESENCE_MSG.setActivity, message);
      },
      // No replay on reconnect, deliberately: the lifecycle only retries while the app is
      // ACTIVE, so a fresh socket always means foreground — which is exactly what the room's
      // `onJoin` already records.
      setForeground: (active) => managed.send(PRESENCE_MSG.setForeground, { active }),
      leave: () => {
        // A spread resync must not fire into a torn-down connection — it would re-read three
        // routes for a session that is over.
        disposed = true;
        if (resync) clearTimeout(resync);
        resync = null;
        managed.leave();
      },
    };
  };
}
